import { ModalButton } from '@/components/common/modal-button';
import PlaceHolder from '@/components/icons/shop/PlaceHolder';
import { RouteIcon } from '@/components/icons';
import type { PoiDetailProps } from '@/components/feature/shop/shop-poi-modal/poi-detail-horizontal';
import shopStyles from '@/components/feature/shop/shop-poi-modal/styles.module.scss';
import { useConfigStore } from '@/stores';
import { useVisibleBoothNumber } from '@/hooks/poi/useVisibleBoothNumber';
import { useVisibleBuildingName } from '@/hooks/poi/useVisibleBuildingName';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { buildFloorGuidePoiDetailTabOrders } from './buildTabOrders';

type Props = {
    poiDetail: PoiDetailProps;
    language: string;
    onGuideStart: () => void;
    carouselCount?: number;
};

export const FloorGuidePoiDetailVertical = ({ poiDetail, language, onGuideStart, carouselCount = 0 }: Props) => {
    const { t } = useTranslation();
    const contrast = useConfigStore((s) => s.config?.ui?.contrast);
    const tabOrders = buildFloorGuidePoiDetailTabOrders(poiDetail, { carouselCount });
    const boothNumber = useVisibleBoothNumber(poiDetail.boothNumber);
    const buildingName = useVisibleBuildingName(poiDetail.buildingName);

    return (
        <div className={clsx(shopStyles['poi-modal__layout'], shopStyles['poi-modal__layout--vertical'])}>
            <div className={shopStyles['poi-modal__header-vertical']}>
                <h1
                    data-interactive
                    data-modal-region
                    tabIndex={tabOrders.name}
                    data-modal-region-order={tabOrders.name}
                    aria-label={poiDetail.poiName}
                    className={clsx(shopStyles['poi-modal__title'], 'focus-border')}
                >
                    {poiDetail.poiName}
                </h1>
                <p
                    className={clsx(shopStyles['poi-modal__meta'], 'focus-border')}
                    data-interactive
                    data-modal-region
                    tabIndex={tabOrders.meta}
                    data-modal-region-order={tabOrders.meta}
                    aria-label={`${poiDetail.floorName}${buildingName ? ` ${buildingName}` : ''}${boothNumber ? ` ${t('shop.search.tts.content.booth', { booth: boothNumber })}` : ''}`}
                >
                    <span>{poiDetail.floorName}</span>
                    {buildingName ? (
                        <>
                            {' '}·<span className="text-[var(--text-black-03)]">{buildingName}</span>
                        </>
                    ) : null}
                    {boothNumber ? <span className={shopStyles['poi-modal__meta--booth']}>{boothNumber}</span> : null}
                </p>
            </div>

            <div className={clsx(shopStyles['poi-modal__media'], shopStyles['poi-modal__media--vertical'], 'focus-border')}>
                {poiDetail.imgUrlDetail ? (
                    <img src={poiDetail.imgUrlDetail} alt={poiDetail.poiName} />
                ) : (
                    <div className={shopStyles['poi-modal__media-empty']}>
                        <PlaceHolder variant="detail" />
                    </div>
                )}
            </div>

            <div className={clsx(shopStyles['poi-modal__panel'], shopStyles['poi-modal__panel--vertical'])}>
                <div
                    className={clsx(shopStyles['poi-modal__content'], shopStyles['poi-modal__content--vertical'], 'scrollbar-custom-gray-99938f scrollbar-w-6')}
                >
                    <div className={shopStyles['poi-modal__rows']}>
                        {poiDetail.listContent?.map((content, index) => (
                            <div
                                className={clsx(shopStyles['poi-modal__row'], 'focus-border')}
                                key={content.key}
                                data-interactive
                                data-modal-region
                                tabIndex={tabOrders.shopInfo[index]}
                                data-modal-region-order={tabOrders.shopInfo[index]}
                                aria-label={`${content.name?.[language]} ${content.content?.[language]}`}
                            >
                                <div className={shopStyles['poi-modal__row-key']}>{content.name?.[language]}</div>
                                <div className={shopStyles['poi-modal__row-name']}>{content.content?.[language]}</div>
                            </div>
                        ))}
                    </div>

                    {poiDetail.desc && (
                        <div className={clsx(shopStyles['poi-modal__desc'], shopStyles['poi-modal__desc--vertical'])} aria-label={poiDetail.desc}>
                            {poiDetail.desc}
                        </div>
                    )}

                    {poiDetail.keywords.length > 0 && (
                        <div className={clsx(shopStyles['poi-modal__tags'], shopStyles['poi-modal__tags--vertical'])}>
                            {poiDetail.keywords.map((keyword) => (
                                <span
                                    key={keyword}
                                    className={clsx(shopStyles['poi-modal__tag'], {
                                        [shopStyles['poi-modal__tag--contrast']]: contrast,
                                    })}
                                    aria-label={keyword}
                                >
                                    {keyword}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                <div className={clsx(shopStyles['poi-modal__actions'], shopStyles['poi-modal__actions--single'])}>
                    <ModalButton
                        size="fit"
                        variant="primary"
                        className={shopStyles['poi-modal__action-button']}
                        type="button"
                        role="button"
                        tabIndex={tabOrders.guide}
                        data-modal-region
                        data-modal-region-order={tabOrders.guide}
                        data-interactive
                        aria-label={t('shop.modal.text.buttons.direction_guide')}
                        icon={<RouteIcon width={28} height={28} />}
                        onClick={onGuideStart}
                        data-role={t('common.data_role.button')}
                    >
                        {t('shop.modal.text.buttons.direction_guide')}
                    </ModalButton>
                </div>
            </div>
        </div>
    );
};
